import { Component, inject, input, output } from "@angular/core";
import { CommonModule } from "@angular/common";
import { PiezeoDashboardService } from "../../../../../app/services/piezo-dashboard.service";
import {
  AbstractControl,
  FormArray,
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from "@angular/forms";
import {
  compareSameAxis,
  compareSameAxisControls,
  duplicateChannelsControlValidator,
  duplicateChannelsValidator,
} from "../../../../../app/directives/form-common-validators";
import { BackendApiService } from "../../../../../app/services/backend-api.service";
import { BaseUrlConstantsService } from "../../../../../app/services/base-url-constants.service";
import { Router } from "@angular/router";

@Component({
  selector: "app-piezo-dashboard-modal",
  templateUrl: "./piezo-dashboard-modal.component.html",
  styleUrl: "./piezo-dashboard-modal.component.scss",
  imports: [CommonModule, ReactiveFormsModule, FormsModule],
  standalone: true,
})
export class PiezoDashboardModal {
  modal_type = input<string>();
  close_modal = output<null>();
  subsystem_id = input<string>();
  client_id = input<string>();

  srv = inject(PiezeoDashboardService);
  service = inject(BackendApiService);
  router = inject(Router);
  private fb = inject(FormBuilder);

  sensorForm!: FormGroup;
  availableAxes = ["X", "Y", "Z"];

  gateways$ = this.srv.gateways$;

  piezo_sensors: any[] = [];
  emsaver_sensors: any[] = [];
  current_sensor: any = {};
  is_editing_sensor: boolean = false;
  old_sensor_channels: number[] = [];
  current_gateway_channels: number[] = [];
  filtered_channels: number[] = [];
  sid = null;

  ngOnInit() {
    this.srv.subsystem_data$.subscribe((data) => {
      console.log("Subsystem data updated in modal:", data);
      this.sid = data?.id;
    });
    this.srv.fetchSubsystemGateways().subscribe();
    switch (this.modal_type()) {
      case "list_gateways":
        this.gateways$.subscribe((gateways) => {
          console.log("Gateways from gateways$:", gateways);
        });
        break;
      case "list_sensors":
        this.fetchSensors();
    }
  }

  // ======================================== EDIT GATEWAY STARTS HERE ========================================
  odr_options = [
    { value: "HIGH", name: "High(3400Hz)" },
    { value: "NORMAL", name: "Normal(1700Hz)" },
    { value: "LOW", name: "Low(200Hz)" },
    { value: "EXTRALOW", name: "Extra Low(100Hz)" },
    { value: "EXTRAEXTRALOW", name: "Extra Extra Low(50Hz)" },
    { value: "ULTRALOW", name: "Ultra Low" },
    { value: "ULTRAULTRALOW", name: "Ultra Ultra Low" },
  ];
  gatewayForm!: FormGroup;
  editing_gateway = false;
  current_gateway: any = null;
  _submitted_gateway_config_form = false;

  odrList = this.odr_options;

  onEditGateway(gateway: any) {
    console.log("Edit gateway:", gateway);

    this.editing_gateway = true;
    this.current_gateway = gateway;

    // Fetch config for this gateway
    this.fetchGatewayConfig();
  }

  fetchGatewayConfig() {
    this.srv
      .fetchGatewayConfig(this.current_gateway.gateway_id)
      .then((config) => {
        console.log("Fetched gateway config:", config);

        this.current_gateway = config[0];

        // IMPORTANT — build form after data arrives
        this.gatewayForm = this.createGatewayForm(
          this.fb,
          this.current_gateway
        );
      })
      .catch((err) => {
        console.error("Error fetching gateway config:", err);
      });
  }

  private createEmptyForm(fb: FormBuilder): FormGroup {
    return fb.group({
      name: ["", Validators.required],
      // disabled mac and serial to match template
      mac: [{ value: "", disabled: true }, Validators.required],
      asset_threshold_status: [0, [Validators.required, Validators.min(0)]],
      scan_rate: [1, [Validators.required, Validators.min(1)]],
      anomaly_counter: [
        1,
        [Validators.required, Validators.min(1), Validators.max(5)],
      ],
      anomaly_analysis: [1, [Validators.required, Validators.min(1)]],
      full_refresh_rate: [1, [Validators.required, Validators.min(1)]],
      summary_refresh_rate: [1, [Validators.required, Validators.min(1)]],
      odr: ["NORMAL", Validators.required],
    });
  }

  createGatewayForm(fb: FormBuilder, data: any): FormGroup {
    return fb.group({
      // Disabled fields
      mac: [
        { value: data?.mac_address ?? "", disabled: true },
        Validators.required,
      ],
      name: [{ value: data?.name ?? "", disabled: true }],
      asset_threshold_status: [
        data?.asset_threshold_status ?? 0,
        [Validators.required, Validators.min(0)],
      ],
      scan_rate: [
        data?.scan_rate ?? 1,
        [Validators.required, Validators.min(1)],
      ],

      // Anomaly counter: min 1, max 5
      anomaly_counter: [
        data?.anomaly_counter ?? 1,
        [Validators.required, Validators.min(1), Validators.max(5)],
      ],

      anomaly_analysis: [
        data?.anomaly_analysis ?? 1,
        [Validators.required, Validators.min(1)],
      ],

      // Refresh rates
      full_refresh_rate: [
        data?.full_refresh_rate ?? 1,
        [Validators.required, Validators.min(1)],
      ],
      summary_refresh_rate: [
        data?.summary_refresh_rate ?? 1,
        [Validators.required, Validators.min(1)],
      ],

      // ODR select
      odr: [data?.odr ?? "NORMAL", Validators.required],
    });
  }

  hasGatewayError(controlName: string, errorType?: string): boolean {
    const control: AbstractControl | null = this.gatewayForm.get(controlName);

    if (!control) return false;

    // only show errors after user touched or after form submit attempt
    const show =
      control.touched || control.dirty || this._submitted_gateway_config_form;

    if (!show) return false;

    if (errorType) {
      return control.hasError(errorType);
    }
    return control.invalid;
  }

  saveGatewayConfig() {
    try {
      if (this.gatewayForm.invalid) {
        this.gatewayForm.markAllAsTouched();
        return;
      }

      const payload = this.gatewayForm.getRawValue();
      console.log("SUBMIT PAYLOAD:", payload);

      // TODO → call update API
      this.srv
        .updateGatewayConfig(payload, this.current_gateway.gateway_id)
        .subscribe(
          (res) => {
            console.log("Gateway config updated successfully:", res);
            this.editing_gateway = false;
            this._submitted_gateway_config_form = false;
            this.current_gateway = null;
            this.gatewayForm = this.createEmptyForm(this.fb); // reset form
            this.srv.fetchSubsystemGateways().subscribe(); // refresh gateways list
          },
          (err) => {
            console.error("Error updating gateway config:", err);
            // Handle error, show notification, etc.
          }
        );
    } catch (error) {
      console.log(error);
    }
  }

  handleGatewayConfigCancel() {
    this.editing_gateway = false;
    this._submitted_gateway_config_form = false;
    // reset form to initial or empty state
    this.gatewayForm = this.createEmptyForm(this.fb);
    this.current_gateway = null;
  }

  // ======================================== EDIT GATEWAY ENDS HERE ========================================
  // ======================================== DELETE GATEWAY STARTS HERE ========================================
  onDeleteGateway(gateway: any) {
    console.log(this.sid);
    console.log("Delete gateway:", gateway);
    this.srv.deleteGateway(gateway.gateway_id, this.sid).subscribe(() => {
      this.srv.fetchSubsystemData(this.sid);
      this.srv.fetchSubsystemGateways();
    });
  }
  // ======================================== DELETE GATEWAY ENDS HERE ========================================

  fetchSensors() {
    try {
      let subsystem_data = null;
      this.srv.subsystem_data$.subscribe((data) => {
        subsystem_data = data;
        if(subsystem_data.length) subsystem_data=subsystem_data[0]
        console.log(subsystem_data)
      });
      if (
        typeof subsystem_data.device_position == "string" &&
        subsystem_data.device_position.trim() !== ""
      ) {
        this.piezo_sensors = JSON.parse(subsystem_data.device_position).filter(
          (s) => s.master_type == 4
        );
        this.emsaver_sensors = JSON.parse(
          subsystem_data.device_position
        ).filter((s) => s.master_type != 4);
      } else {
        this.piezo_sensors = subsystem_data.device_position.filter(
          (s) => s.master_type == 4
        );
        this.emsaver_sensors = subsystem_data.device_position.filter(
          (s) => s.master_type != 4
        );
      }
      console.log(this.piezo_sensors)
      console.log(this.emsaver_sensors)
    } catch (error) {
      console.log(error);
    }
  }

  // Helper function to get axis keys from sensors_info
  getAxisKeys(sensorsInfo: any): string[] {
    if (!sensorsInfo) return [];

    // Filter keys to only include axis and channel information
    return Object.keys(sensorsInfo)
      .filter((key) => key.includes("axis") || key.includes("channel"))
      .sort((a, b) => {
        // Sort to show sensor_1_axis, sensor_1_channel, sensor_2_axis, sensor_2_channel, etc.
        const numA = parseInt(a.match(/\d+/)?.[0] || "0");
        const numB = parseInt(b.match(/\d+/)?.[0] || "0");
        if (numA !== numB) return numA - numB;
        // If same sensor number, show axis before channel
        return a.includes("axis") ? -1 : 1;
      });
  }

  // Helper function to format axis key labels
  getAxisLabel(key: string): string {
    if (!key) return "";

    // Extract sensor number and type (axis or channel)
    const matches = key.match(/sensor_(\d+)_(axis|channel)/);
    if (!matches) return key;

    const sensorNum = matches[1];
    const type = matches[2];

    // Format as "Sensor 1 Axis" or "Sensor 1 Channel"
    return `S${sensorNum} ${type.charAt(0).toUpperCase() + type.slice(1)}`;
  }

  onEditSensor(sensor) {
    this.current_sensor = sensor;
    this.is_editing_sensor = true;
    this.getGatewayAvailableChannels(sensor.gateway_id);
    this.initializeForm();
  }

  getGatewayAvailableChannels(gateway_id: string) {
    this.srv.gateways$.subscribe((gateways) => {
      console.log("GATEWAYS:", gateways);

      const gateway = gateways.find((gw) => gw.gateway_id == gateway_id);

      if (gateway) {
        console.log("FOUND GATEWAY:", gateway);
        // do your logic here...
        this.current_gateway_channels = gateway.available_channels || [];
        this.filtered_channels = this.current_gateway_channels;
      } else {
        this.current_gateway_channels = [];
      }
    });
  }

  initializeForm() {
    const sensorData = this.current_sensor;
    console.log(sensorData);
    this.sensorForm = this.fb.group({
      mac_address: [
        { value: sensorData.mac_address || "", disabled: true },
        Validators.required,
      ],
      serial_number: [
        { value: sensorData.serial_number || "", disabled: true },
        Validators.required,
      ],
      sensorDNames: [sensorData.sensorDNames || "", Validators.required],
      sensorMarker: [sensorData.sensorMarker || "", Validators.required],
      assetMarker: [sensorData.assetMarker || "", Validators.required],

      no_of_axis: [
        sensorData.no_of_axis,
        [Validators.required, Validators.min(1), Validators.max(3)],
      ],

      is_temperature_added: [sensorData.is_temperature_added || 0],

      temperature_channel: [
        {
          value: sensorData.temperature_channel,
          disabled: !sensorData.is_temperature_added,
        },
        sensorData.is_temperature_added ? Validators.required : [],
      ],

      gateway_id: [
        { value: sensorData.gateway_id || "", disabled: true },
        Validators.required,
      ],

      assetType: [
        { value: sensorData.assetType || "", disabled: true },
        Validators.required,
      ],

      subType: [
        { value: sensorData.subType || "", disabled: true },
        Validators.required,
      ],

      axis_mappings: this.fb.array([]),
    });

    // Watch for changes in is_temperature_added
    this.sensorForm
      .get("is_temperature_added")
      ?.valueChanges.subscribe((value) => {
        const tempChannelControl = this.sensorForm.get("temperature_channel");
        if (value === 1) {
          tempChannelControl?.enable();
          tempChannelControl?.setValidators([Validators.required]);
        } else {
          tempChannelControl?.disable();
          tempChannelControl?.clearValidators();
          tempChannelControl?.setValue("");
        }
        tempChannelControl?.updateValueAndValidity();
      });

    // Initialize axis mappings based on no_of_axis
    this.updateAxisMappings(sensorData.no_of_axis, true);

    // Watch for changes in no_of_axis
    this.sensorForm.get("no_of_axis")?.valueChanges.subscribe((value) => {
      this.updateAxisMappings(value);
    });

    this.old_sensor_channels = this.sensorForm
      .getRawValue()
      .axis_mappings.map((m: any) => m.channel);
    this.filtered_channels = [
      ...this.old_sensor_channels,
      ...this.current_gateway_channels,
    ];
    if (sensorData.temperature_channel >= 1) {
      this.filtered_channels.push(sensorData.temperature_channel);
    }

    // Re-run validator whenever ANY axis/channel/temperature field changes
    this.sensorForm.get("axis_mappings")?.valueChanges.subscribe(() => {
      this.reapplyValidators();
    });

    this.sensorForm.get("temperature_channel")?.valueChanges.subscribe(() => {
      this.reapplyValidators();
    });

    this.sensorForm.get("is_temperature_added")?.valueChanges.subscribe(() => {
      this.reapplyValidators();
    });
  }

  private reapplyValidators() {
    const axisControls = this.axisMappings.controls.map((c) => c.get("axis")!);

    const isTempAdded = this.sensorForm.get("is_temperature_added")?.value == 1;

    const channelControls = [
      ...this.axisMappings.controls.map((c) => c.get("channel")!),
      ...(isTempAdded ? [this.sensorForm.get("temperature_channel")!] : []),
    ];

    this.axisMappings.setValidators([
      compareSameAxisControls(axisControls),
      duplicateChannelsControlValidator(channelControls),
    ]);

    this.axisMappings.updateValueAndValidity({ emitEvent: false });
  }

  get axisMappings(): FormArray {
    return this.sensorForm.get("axis_mappings") as FormArray;
  }

  updateAxisMappings(numberOfAxis: number, isInitial = false) {
    const sensorData = this.current_sensor;
    const sensorsInfo = sensorData.sensors_info || {};
    let temp_channel: number | null = null;
    if (!isInitial) {
      // Preserve existing mappings
      const existing_channels = this.axisMappings.controls.map(
        (control) => control.get("channel")?.value
      );
      if (this.sensorForm.get("is_temperature_added")?.value == 1) {
        temp_channel = this.sensorForm.get("temperature_channel")?.value;
      }
      let uniqueChannels = [
        ...new Set([
          ...existing_channels,
          ...this.old_sensor_channels,
          ...this.current_gateway_channels,
          ...(temp_channel ? [temp_channel] : []),
        ]),
      ];
      this.filtered_channels = uniqueChannels;
    }

    // Clear existing mappings
    while (this.axisMappings.length > 0) {
      this.axisMappings.removeAt(0);
    }

    // Add new mappings based on number of axis
    for (let i = 1; i <= numberOfAxis; i++) {
      const axisKey = `sensor_${i}_axis`;
      const channelKey = `sensor_${i}_channel`;

      this.axisMappings.push(
        this.fb.group({
          sensor_number: [i],
          axis: [sensorsInfo[axisKey] || "", Validators.required],
          channel: [
            sensorsInfo[channelKey] || "",
            [Validators.required, Validators.min(1)],
          ],
        })
      );
    }

    const axisControls = this.axisMappings.controls.map((c) => c.get("axis")!);
    const isTempAdded = this.sensorForm.get("is_temperature_added")?.value == 1;

    const channelControls = [
      ...this.axisMappings.controls.map((c) => c.get("channel")),
      ...(isTempAdded ? [this.sensorForm.get("temperature_channel")] : []),
    ];
    this.axisMappings.setValidators([
      compareSameAxisControls(axisControls),
      duplicateChannelsControlValidator(channelControls),
    ]);

    this.axisMappings.updateValueAndValidity();
  }

  getAvailableAxesForIndex(index: number): string[] {
    const selectedAxes = this.axisMappings.controls
      .map((control, i) => (i !== index ? control.get("axis")?.value : null))
      .filter((axis) => axis);

    return this.availableAxes.filter((axis) => !selectedAxes.includes(axis));
  }

  isAxisSelected(axis: string, currentIndex: number): boolean {
    return this.axisMappings.controls.some(
      (control, i) => i !== currentIndex && control.get("axis")?.value === axis
    );
  }

  handleSubmit() {
    if (this.sensorForm.valid) {
      const formValue = this.sensorForm.getRawValue();

      // Transform axis_mappings back to sensors_info format
      const sensorsInfo: any = {};
      formValue.axis_mappings.forEach((mapping: any) => {
        sensorsInfo[`sensor_${mapping.sensor_number}_axis`] = mapping.axis;
        sensorsInfo[`sensor_${mapping.sensor_number}_channel`] =
          mapping.channel;
      });

      const updatedSensor = {
        ...this.current_sensor,
        ...formValue,
        sensors_info: sensorsInfo,
        is_temperature_added: formValue.is_temperature_added ? 1 : 0,
        old_sensor_channels: this.old_sensor_channels,
        old_temperature_channel: this.current_sensor.temperature_channel,
        is_master_change:
          this.current_sensor.assetMarker != formValue.assetMarker ? 1 : 0,
      };
      delete updatedSensor.axis_mappings;

      this.srv
        .updateSensorInSubsystem(updatedSensor, this.sid)
        .subscribe(() => {
          this.is_editing_sensor = false;
          this.srv.fetchSubsystemData(this.sid).subscribe(() => {
            this.fetchSensors();
          });
        });

      // this.onSave.emit(updatedSensor);
    } else {
      // Mark all fields as touched to show validation errors
      Object.keys(this.sensorForm.controls).forEach((key) => {
        this.sensorForm.get(key)?.markAsTouched();
      });

      this.axisMappings.controls.forEach((control) => {
        Object.keys(control.value).forEach((key) => {
          control.get(key)?.markAsTouched();
        });
      });
    }
  }

  handleCancel() {
    this.close_modal.emit(null);
  }

  // Helper method to check if field has error
  hasError(fieldName: string, errorType?: string): boolean {
    const field = this.sensorForm.get(fieldName);
    if (!field) return false;

    if (errorType) {
      return field.hasError(errorType) && (field.dirty || field.touched);
    }
    return field.invalid && (field.dirty || field.touched);
  }

  // Helper method to check if axis mapping has error
  hasAxisMappingError(index: number, fieldName: string): boolean {
    const control = this.axisMappings.at(index).get(fieldName);
    return control
      ? control.invalid && (control.dirty || control.touched)
      : false;
  }

  // ===================================DELETE SENSOR STARTS========================================
  onDeleteSensor(sensor: any) {
    if (sensor.master_type == 4) {
      this.srv
        .deleteSingleSensor(sensor.asset_id, sensor.gateway_id)
        .subscribe(() => {
          this.srv.fetchSubsystemData(this.sid).subscribe(() => {
            this.fetchSensors();
          });
        });
    } else if (sensor.master_type != 4) {
      this.srv.deleteSingleEmSensor(sensor.asset_id, this.sid).subscribe(() => {
        this.srv.fetchSubsystemData(this.sid).subscribe(() => {
          this.fetchSensors();
        });
      });
    }
  }
  // ===================================DELETE SENSOR ENDS==========================================

  // ===================================DELETE POWERTRAIN STARTS====================================

  deletePiezoAsset(): void {
    this.service
      .deleteInfo(
        `${BaseUrlConstantsService.BASE_PIEZO_SERVICE}/api/delete-subsystem/${this.subsystem_id()}/${this.client_id()}`
      )
      .then((res) => {
        const response = JSON.parse(JSON.stringify(res));
        if (response.code === 200 || response.status === "success") {
          // Remove asset from lists
          this.service.showSuccess("Asset deleted successfully");
          let navigationExtras = {
            queryParams: { powertrain: true },
          };
          if(localStorage.getItem('ROLE')=='ADMIN'){
            this.router.navigate([`/device-report-new`], navigationExtras);
          }else{
            this.router.navigate([`/device-reports`], navigationExtras);
          }
        } else {
          this.service.showError("Failed to delete asset");
        }
      })
      .catch((error) => {
        console.error("Error deleting asset:", error);
        alert("Error deleting asset. Please try again.");
      });
  }

  // ===================================DELETE POWERTRAIN ENDS======================================
}
